import { observer } from 'mobx-react-lite';
import React from 'react';
import { BookingSummaryEditLink } from '#/components/BookingSummaryPanel/components/BookingSummaryEditLink';
import BookingSummaryPanelSection, { BookingSummaryPanelSectionProps } from '#/components/BookingSummaryPanel/components/BookingSummaryPanelSection';
import { useCanEditSelectedReservation } from '#/hooks/useCanEditSelectedReservation';
import { useGetBranchByCode } from '#/hooks/useGetBranchByCode';
import { useGetReservationEntities } from '#/hooks/useGetReservationEntities';
import { amendmentStore } from '#/stores/amendmentStore';
import { RentalsReservationInstance } from '@jucy-nakuru/common/services/JucyRentalsApi/RentalsReservation';
import { useGetTripEntities } from '@jucy-ui/common/hooks/useGetTripEntities';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { TripInfoLocationDateSummary } from './TripInfoLocationDateSummary';

interface TripInfoSummaryProps extends BookingSummaryPanelSectionProps {
    reservation: RentalsReservationInstance;
    showEdit?: boolean;
}

export const TripInfoSummary: React.FC<TripInfoSummaryProps> = observer(function TripInfoSummaryComponent({
    reservation,
    showEdit = false,
    ...props
}) {
    const amendment = amendmentStore.amendments.find((r) => r.reference === reservation?.reservationReference);
    const { data: reservationData } = useGetReservationEntities({ reservation });
    const { data: amendmentData } = useGetTripEntities({ trip: amendment?.updates });
    const { data: pickUpBranch } = useGetBranchByCode(reservation.pickUpLocationCode);
    const { data: dropOffBranch } = useGetBranchByCode(reservation.dropOffLocationCode);
    const isEditable = useCanEditSelectedReservation(showEdit);

    return (
        <BookingSummaryPanelSection
            edit={
                isEditable ? (
                    <BookingSummaryEditLink href={`/self-service/reservation/${reservation.reservationReference}/edit?action=trip-details`} />
                ) : null
            }
            {...props}
        >
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    mb: isEditable ? 0 : 2,
                    justifyContent: 'space-between',
                }}
            >
                <TripInfoLocationDateSummary
                    title="Pick Up"
                    site={pickUpBranch || reservationData?.pickUpBranch}
                    date={reservation?.pickUpDate}
                    amendedDate={isEditable ? amendment?.updates?.pickUpDate : undefined}
                    amendedSite={isEditable ? amendmentData?.pickUpBranch : undefined}
                    componentProps={{ root: { sx: { flex: '1 1' } } }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ArrowRightIcon />
                </Box>
                <TripInfoLocationDateSummary
                    title="Drop Off"
                    site={dropOffBranch || reservationData?.dropOffBranch}
                    date={reservation?.dropOffDate}
                    amendedDate={isEditable ? amendment?.updates?.dropOffDate : undefined}
                    amendedSite={isEditable ? amendmentData?.dropOffBranch : undefined}
                    componentProps={{ root: { sx: { flex: '1 1' } } }}
                />
            </Stack>
        </BookingSummaryPanelSection>
    );
});
